import type { CustomerInteractionNextAction } from "@repo/app-types/entities/teammatePerformanceSummary";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { IconPlayerPause } from "@tabler/icons-react";
import type { FC } from "react";
import {
  formatCallActivityTimestamp,
  nextActionLabel,
} from "./callActivityColumns.js";

export interface GradingScheduledCallBannerProps {
  nextAction: CustomerInteractionNextAction;
  /** Who the call is with, for the banner copy. */
  customerName: string | undefined;
}

/**
 * What an interaction's call task will do next. Shown above the assessment,
 * which is empty until a call actually happens. Stopping the task lives in the
 * interaction's header, so it's there whatever the task is doing.
 */
export const GradingScheduledCallBanner: FC<GradingScheduledCallBannerProps> = (
  props,
) => {
  const { nextAction, customerName } = props;

  const isPaused =
    nextAction.type === "paused" || nextAction.type === "monitoring_paused";
  const isMonitoring =
    nextAction.type === "monitoring" || nextAction.type === "monitoring_paused";
  const scheduledAt = formatCallActivityTimestamp(nextAction.scheduled_at);

  return (
    <InfoBanner
      icon={
        isPaused ? (
          <IconPlayerPause aria-hidden className="size-5 text-blue-400" />
        ) : undefined
      }
      title={
        isMonitoring
          ? nextActionLabel[nextAction.type]
          : `${nextActionLabel[nextAction.type]} • Attempt ${nextAction.attempt_number}`
      }
      description={
        isPaused && isMonitoring ? (
          <>
            Outbound outreach is off for this agent. Re-enable it by{" "}
            <time dateTime={nextAction.expires_at}>
              {formatCallActivityTimestamp(nextAction.expires_at)}
            </time>{" "}
            to resume this outreach. No call is scheduled.
          </>
        ) : isPaused ? (
          <>
            Outbound calling is off for this agent, so the call planned for{" "}
            <time dateTime={nextAction.scheduled_at}>{scheduledAt}</time> will
            not be placed. Re-enable the agent by{" "}
            <time dateTime={nextAction.expires_at}>
              {formatCallActivityTimestamp(nextAction.expires_at)}
            </time>{" "}
            to keep it.
          </>
        ) : isMonitoring ? (
          <>
            The agent is monitoring its outreach to{" "}
            {customerName || "this customer"}. It checks back by{" "}
            <time dateTime={nextAction.scheduled_at}>{scheduledAt}</time>, or
            sooner if they reply, and decides then whether anything else is
            needed.
          </>
        ) : (
          <>
            A call with {customerName || "this customer"} is scheduled for{" "}
            <time dateTime={nextAction.scheduled_at}>{scheduledAt}</time>.
          </>
        )
      }
    />
  );
};
